import React from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Target, Clock, Repeat, CalendarCheck2, ShieldAlert, ArrowRight, Sparkles } from "lucide-react";
import type { DreamPlan, GoalStepsProgress } from "@/lib/dreamBridge";
interface PlanCardsProps {
  plan: DreamPlan;
  goalSteps: [GoalStepsProgress, GoalStepsProgress, GoalStepsProgress];
  daysDone: [boolean, boolean, boolean, boolean, boolean, boolean, boolean];
  onToggleGoalStep: (goalIndex: number, stepIndex: number) => void;
  onToggleDayDone: (dayIndex: number) => void;
}
const STEP_LABELS = ["Started", "Halfway there", "Done for now"] as const;
export default function PlanCards({
  plan,
  goalSteps,
  daysDone,
  onToggleGoalStep,
  onToggleDayDone
}: PlanCardsProps) {
  return <div className="space-y-8">
      {/* 1. Three Concrete Goal Cards */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Target className="size-4 text-primary shrink-0" />
          <h4 className="font-serif text-lg sm:text-xl font-bold text-foreground">
            Three 4-to-8 Week Grounding Goals
          </h4>
        </div>
        <p className="text-xs text-muted-foreground">
          Tangible markers anchored to your chosen values. Check off each milestone as you progress.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 pt-1">
          {plan.goals.map((goal, goalIdx) => {
          const stepsProgress = goalSteps[goalIdx] || [false, false, false];
          const stepsCompletedCount = stepsProgress.filter(Boolean).length;
          return <div key={goal.id || goalIdx} className="ql-glass p-5 rounded-2xl border border-primary/30 flex flex-col justify-between space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-xs font-bold text-primary px-2.5 py-0.5 rounded-full bg-primary/10 border border-primary/30">
                      Goal 0{goalIdx + 1}
                    </span>
                    <span className="font-mono text-[11px] text-muted-foreground">
                      {stepsCompletedCount} of 3 done
                    </span>
                  </div>

                  <h5 className="font-serif text-base font-bold text-foreground leading-snug break-words">
                    {goal.title}
                  </h5>

                  <p className="text-xs text-muted-foreground leading-relaxed break-words">
                    <strong className="text-foreground font-semibold">Why this matters: </strong>
                    {goal.why}
                  </p>

                  <div className="p-3 rounded-xl bg-card/80 border border-border/60 text-xs text-foreground/90 space-y-1">
                    <span className="font-mono text-[11px] text-primary font-bold block">
                      Observable Marker:
                    </span>
                    <p className="leading-relaxed break-words">{goal.measure}</p>
                  </div>
                </div>

                {/* 3 Progress Stages Checkpoints */}
                <div className="pt-3 border-t border-border/60 space-y-2">
                  <span className="text-[11px] font-mono text-muted-foreground font-semibold block uppercase tracking-wider">
                    Progress Milestones:
                  </span>
                  <div className="grid grid-cols-1 gap-2">
                    {STEP_LABELS.map((stageLabel, stepIdx) => {
                  const isChecked = stepsProgress[stepIdx] || false;
                  const checkId = `goal-${goalIdx}-step-${stepIdx}`;
                  return <div key={stepIdx} className={`dtr-goal-pill flex items-center gap-3 p-2.5 rounded-xl border transition-all ${isChecked ? "bg-primary/10 border-primary text-foreground shadow-[0_0_8px_var(--glow-primary)]" : "bg-card/40 border-border hover:border-primary/40 text-muted-foreground"}`} onClick={e => {
                    // If the event came from clicking the inner Checkbox or Label, do not trigger a second toggle
                    const target = e.target as HTMLElement;
                    if (target.closest('button[role="checkbox"]') || target.closest('label')) {
                      return;
                    }
                    onToggleGoalStep(goalIdx, stepIdx);
                  }}>
                          <div className="dtr-step-checkbox">
                            <Checkbox id={checkId} checked={isChecked} onCheckedChange={() => onToggleGoalStep(goalIdx, stepIdx)} aria-label={`${goal.title} — ${stageLabel}`} className="size-4" />
                          </div>
                          <Label htmlFor={checkId} className="text-xs font-medium cursor-pointer flex-1 leading-normal">
                            <span className="sr-only">{goal.title} — </span>
                            <span className={isChecked ? "font-semibold text-foreground" : ""}>
                              {stageLabel}
                            </span>
                          </Label>
                        </div>;
                })}
                  </div>
                </div>
              </div>;
        })}
        </div>
      </div>

      {/* 2. Side-by-Side: 10-Minute Immediate Move & Anchoring Habit */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Ten-minute action card */}
        <div className="ql-glass p-5 sm:p-6 rounded-2xl border border-primary/30 space-y-4">
          <div className="flex items-center gap-2">
            <Clock className="size-4 text-primary shrink-0" />
            <h4 className="font-serif text-base sm:text-lg font-bold text-foreground">
              Do This in the Next 10 Minutes
            </h4>
          </div>
          <p className="text-xs text-muted-foreground">
            A concrete, immediate micro-step to break inertia while this intention is fresh.
          </p>

          <div className="p-3.5 rounded-xl bg-primary/10 border border-primary/40 shadow-[0_0_12px_var(--glow-primary)]">
            <h5 className="font-serif text-sm font-bold text-foreground">
              {plan.tenMinuteAction.title}
            </h5>
          </div>

          <ol className="space-y-2 pt-1">
            {plan.tenMinuteAction.steps.map((step, idx) => <li key={idx} className="flex items-start gap-2.5 text-xs text-foreground/90 leading-relaxed">
                <span className="size-5 rounded-full bg-primary/20 border border-primary/40 text-primary font-mono text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                  {idx + 1}
                </span>
                <span className="flex-1 break-words">{step}</span>
              </li>)}
          </ol>
        </div>

        {/* Anchoring habit card */}
        <div className="ql-glass p-5 sm:p-6 rounded-2xl border border-primary/30 space-y-4">
          <div className="flex items-center gap-2">
            <Repeat className="size-4 text-primary shrink-0" />
            <h4 className="font-serif text-base sm:text-lg font-bold text-foreground">
              Anchoring Habit
            </h4>
          </div>
          <p className="text-xs text-muted-foreground">
            A predictable loop anchored to an existing daily or weekly trigger.
          </p>

          <div className="space-y-3 pt-1">
            <div className="p-3.5 rounded-xl bg-card/70 border border-border/80 space-y-1">
              <span className="font-mono text-[10px] uppercase font-bold text-primary tracking-wider">
                Habit Pattern ({plan.habit.cadence})
              </span>
              <p className="text-xs sm:text-sm text-foreground font-medium leading-relaxed break-words">
                {plan.habit.text}
              </p>
            </div>

            <div className="p-3.5 rounded-xl bg-card/70 border border-border/80 space-y-1">
              <span className="font-mono text-[10px] uppercase font-bold text-primary tracking-wider">
                Habit Cue (Trigger)
              </span>
              <p className="text-xs text-muted-foreground leading-relaxed break-words">
                {plan.habit.cue}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 3. Seven-Day Starter Plan */}
      <div className="ql-glass p-5 sm:p-7 rounded-2xl border border-primary/30 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-border/60 pb-3">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <CalendarCheck2 className="size-4 text-primary shrink-0" />
              <h4 className="font-serif text-base sm:text-lg font-bold text-foreground">
                Seven-Day Starter Sequence
              </h4>
            </div>
            <p className="text-xs text-muted-foreground">
              Seven gentle, progressive steps. Check off each day as you carry it out.
            </p>
          </div>
          <span className="font-mono text-xs text-primary px-3 py-1 rounded-full bg-primary/10 border border-primary/30 self-start sm:self-auto">
            {daysDone.filter(Boolean).length} / 7 days done
          </span>
        </div>

        <div className="space-y-2 pt-1">
          {plan.sevenDay.map((step, idx) => {
          const isDone = daysDone[idx] || false;
          const dayCheckId = `seven-day-step-${idx}`;
          return <div key={step.day || idx} className={`dtr-seven-day-row p-3 sm:p-3.5 rounded-xl border flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${isDone ? "bg-primary/10 border-primary shadow-[0_0_8px_var(--glow-primary)]" : "bg-card/40 border-border"}`}>
                <div className="flex items-start sm:items-center gap-3 min-w-0 flex-1">
                  <div className="dtr-step-checkbox shrink-0">
                    <Checkbox id={dayCheckId} checked={isDone} onCheckedChange={() => onToggleDayDone(idx)} aria-label={`Day ${step.day}: ${step.title}`} className="size-4" />
                  </div>

                  <div className="min-w-0 flex-1 space-y-0.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs font-bold text-primary">
                        Day {step.day}
                      </span>
                      <span className="text-muted-foreground text-xs font-mono">·</span>
                      <Label htmlFor={dayCheckId} className={`text-xs sm:text-sm font-semibold cursor-pointer ${isDone ? "line-through text-muted-foreground" : "text-foreground"}`}>
                        {step.title}
                      </Label>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed break-words">
                      {step.task}
                    </p>
                  </div>
                </div>

                <div className="self-end sm:self-center shrink-0 pl-11 sm:pl-0">
                  <span className="font-mono text-[11px] text-primary/80 px-2.5 py-1 rounded-full bg-primary/10 border border-primary/20">
                    ~{step.minutes} mins
                  </span>
                </div>
              </div>;
        })}
        </div>
      </div>

      {/* 4. Tailored Obstacles & Strategic Countermeasures */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <ShieldAlert className="size-4 text-primary shrink-0" />
          <h4 className="font-serif text-lg font-bold text-foreground">
            Anticipated Obstacles & Practical Responses
          </h4>
        </div>
        <p className="text-xs text-muted-foreground">
          Friction is normal. Having a pre-planned response prevents discouragement when life interrupts.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-1">
          {plan.obstacles.map(obs => <div key={obs.id} className="ql-glass p-5 rounded-2xl border border-border/80 space-y-3 flex flex-col justify-between">
              <div className="space-y-2">
                <span className="font-mono text-[10px] uppercase font-bold text-destructive tracking-wider block">
                  Obstacle
                </span>
                <h5 className="font-serif text-sm font-bold text-foreground leading-snug">
                  {obs.label}
                </h5>
              </div>

              <div className="space-y-2 pt-2 border-t border-border/60">
                <span className="font-mono text-[10px] uppercase font-bold text-primary tracking-wider block">
                  Grounding Response:
                </span>
                <ul className="space-y-2 text-xs text-foreground/90 leading-relaxed">
                  {obs.response.map((res, rIdx) => <li key={rIdx} className="flex items-start gap-2">
                      <ArrowRight className="size-3 text-primary shrink-0 mt-1" />
                      <span className="break-words">{res}</span>
                    </li>)}
                </ul>
              </div>
            </div>)}
        </div>
      </div>

      {/* 5. Imagined vs. Changeable Two-Column Panel */}
      <div className="ql-glass p-5 sm:p-7 rounded-2xl border border-primary/30 space-y-5">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Sparkles className="size-4 text-primary shrink-0" />
            <h4 className="font-serif text-base sm:text-lg font-bold text-foreground">
              Imagined vs. Changeable
            </h4>
          </div>
          <p className="text-xs text-muted-foreground">
            Distinguishing fantasy's effortless allure from the real-world agency that builds lasting substance.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-1">
          {/* Imagined column */}
          <div className="p-4 sm:p-5 rounded-xl bg-card/60 border border-border/70 space-y-3">
            <div className="flex items-center gap-2">
              <span className="size-2 rounded-full bg-chart-3 shrink-0" />
              <h5 className="font-serif text-xs sm:text-sm font-bold text-chart-3 uppercase tracking-wider">
                What the Imagined Self Offers (Fantasy Realm)
              </h5>
            </div>
            <ul className="space-y-2 text-xs text-muted-foreground leading-relaxed">
              {plan.imaginedVsChangeable.imagined.map((item, idx) => <li key={idx} className="flex items-start gap-2">
                  <span className="text-chart-3 font-mono text-[11px] font-bold shrink-0 mt-0.5">•</span>
                  <span className="break-words">{item}</span>
                </li>)}
            </ul>
          </div>

          {/* Changeable column */}
          <div className="p-4 sm:p-5 rounded-xl bg-card/60 border border-primary/40 space-y-3 shadow-[0_0_12px_var(--glow-primary)]">
            <div className="flex items-center gap-2">
              <span className="size-2 rounded-full bg-primary shrink-0" />
              <h5 className="font-serif text-xs sm:text-sm font-bold text-primary uppercase tracking-wider">
                What You Can Actually Change (Lived Reality)
              </h5>
            </div>
            <ul className="space-y-2 text-xs text-foreground/90 leading-relaxed">
              {plan.imaginedVsChangeable.changeable.map((item, idx) => <li key={idx} className="flex items-start gap-2">
                  <span className="text-primary font-mono text-[11px] font-bold shrink-0 mt-0.5">•</span>
                  <span className="break-words">{item}</span>
                </li>)}
            </ul>
          </div>
        </div>

        {plan.imaginedVsChangeable.note && <div className="p-3.5 rounded-xl bg-card/80 border border-border/60 text-xs text-muted-foreground italic text-center leading-relaxed">
            "{plan.imaginedVsChangeable.note}"
          </div>}
      </div>

      {/* 6. Honest Limit Notice */}
      <div className="p-4 sm:p-5 rounded-xl bg-card/60 border border-border/80 text-xs text-muted-foreground space-y-1.5 leading-relaxed">
        <span className="font-mono text-[11px] font-bold text-foreground uppercase tracking-wider block">
          Honest Limits of This Plan:
        </span>
        <p className="break-words">{plan.honestLimit}</p>
      </div>
    </div>;
}